import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { cartAPI } from '../api/cart.api';
import { hasToken } from '../utils/token';
import { getDisplayPrice } from '../utils/productPricing';

const CartWishlistContext = createContext(null);

const LOCAL_CART_KEY = 'sifrifana_cart';
const LOCAL_WISHLIST_KEY = 'sifrifana_wishlist';

const getProductId = (item) => item?.id || item?._id || item?.productId;
const getCartItemSyncId = (item) => item?.cartItemId || item?.itemId || getProductId(item);

export const useCartWishlist = () => {
  const context = useContext(CartWishlistContext);
  if (!context) {
    throw new Error('useCartWishlist must be used within CartWishlistProvider');
  }
  return context;
};

export const CartWishlistProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const localCart = window.localStorage.getItem(LOCAL_CART_KEY);
    const localWishlist = window.localStorage.getItem(LOCAL_WISHLIST_KEY);
    if (localCart) {
      try {
        setCartItems(JSON.parse(localCart));
      } catch (error) {
        setCartItems([]);
      }
    }
    if (localWishlist) {
      try {
        setWishlistItems(JSON.parse(localWishlist));
      } catch (error) {
        setWishlistItems([]);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    window.localStorage.setItem(LOCAL_CART_KEY, JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    window.localStorage.setItem(LOCAL_WISHLIST_KEY, JSON.stringify(wishlistItems));
  }, [wishlistItems]);

  const cartTotal = useMemo(() => {
    const subtotal = cartItems.reduce((acc, item) => acc + getDisplayPrice(item.product || item) * (item.quantity || item.qty || 1), 0);
    return { subtotal, total: subtotal };
  }, [cartItems]);

  const addToCart = async (product) => {
    const id = getProductId(product);
    if (!id) return;
    setCartItems((prev) => {
      const existing = prev.find((item) => getProductId(item) === id);
      if (existing) {
        return prev.map((item) =>
          getProductId(item) === id
            ? { ...item, id, quantity: (item.quantity || item.qty || 1) + 1 }
            : item
        );
      }
      return [...prev, { ...product, id, quantity: 1 }];
    });

    if (!hasToken()) {
      return;
    }

    try {
      const data = await cartAPI.addItem({ productId: id, qty: 1 });
      const syncedItem = data?.cart?.items?.find((item) => String(item.productId) === String(id));

      if (syncedItem?.id) {
        setCartItems((prev) =>
          prev.map((item) =>
            getProductId(item) === id ? { ...item, cartItemId: syncedItem.id } : item
          )
        );
      }
    } catch (error) {
      console.warn('Could not sync cart to server:', error);
    }
  };

  const removeFromCart = async (productId) => {
    const itemToRemove = cartItems.find((item) => getProductId(item) === productId || getCartItemSyncId(item) === productId);
    const syncId = itemToRemove?.cartItemId || itemToRemove?.itemId;

    setCartItems((prev) => prev.filter((item) => getProductId(item) !== productId && getCartItemSyncId(item) !== productId));

    if (!syncId) {
      return;
    }

    try {
      await cartAPI.removeItem(syncId);
    } catch (error) {
      console.warn('Could not remove cart item from server:', error);
    }
  };

  const updateCartItem = async (productId, quantity) => {
    const itemToUpdate = cartItems.find((item) => getProductId(item) === productId || getCartItemSyncId(item) === productId);
    const syncId = itemToUpdate?.cartItemId || itemToUpdate?.itemId;

    setCartItems((prev) =>
      prev.map((item) =>
        getProductId(item) === productId || getCartItemSyncId(item) === productId ? { ...item, quantity } : item
      )
    );

    if (!syncId) {
      return;
    }

    try {
      await cartAPI.updateItem(syncId, { qty: quantity });
    } catch (error) {
      console.warn('Could not update cart item on server:', error);
    }
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const addToWishlist = (product) => {
    const id = getProductId(product);
    if (!id) return;
    setWishlistItems((prev) => {
      if (prev.find((item) => getProductId(item) === id)) return prev;
      return [...prev, { ...product, id }];
    });
  };

  const removeFromWishlist = (productId) => {
    setWishlistItems((prev) => prev.filter((item) => getProductId(item) !== productId));
  };

  const value = {
    cartItems,
    wishlistItems,
    cartTotal,
    loading,
    addToCart,
    removeFromCart,
    updateCartItem,
    clearCart,
    addToWishlist,
    removeFromWishlist,
  };

  return <CartWishlistContext.Provider value={value}>{children}</CartWishlistContext.Provider>;
};
